import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { UserCircle, LogOut } from "lucide-react";

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [location] = useLocation();
  const { user, logoutMutation } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when clicking a link
  const handleLinkClick = (hash?: string) => {
    setIsMenuOpen(false);
    if (hash && location === '/') {
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  return (
    <header className={cn(
      "fixed w-full z-50 transition-all duration-300",
      isScrolled ? "shadow-elegant py-2 bg-white" : "py-0 bg-transparent"
    )}>
      {/* Top contact bar */}
      <div className="bg-[#1a365d] py-2 text-white text-sm hidden md:block">
        <div className="container mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center space-x-6">
            <span className="flex items-center text-white/80">
              <i className="fas fa-globe-americas mr-2 text-[#7d9b76]"></i> Serving Clients Nationwide
            </span>
            <a href="tel:8017916900" className="flex items-center hover:text-[#7d9b76] transition-colors duration-300">
              <i className="fas fa-phone-alt mr-2 text-[#7d9b76]"></i> 801-791-6900
            </a>
            <a href="mailto:sarah@swaccountingpro.com" className="flex items-center hover:text-[#7d9b76] transition-colors duration-300">
              <i className="fas fa-envelope mr-2 text-[#7d9b76]"></i> sarah@swaccountingpro.com
            </a>
          </div>
          <div className="flex items-center space-x-4">
            <a 
              href="https://www.facebook.com/profile.php?id=100054204701084" 
              className="hover:text-[#7d9b76] transition-colors duration-300" 
              aria-label="Facebook"
            >
              <i className="fab fa-facebook-f"></i>
            </a>
            <a 
              href="https://linkedin.com" 
              className="hover:text-[#7d9b76] transition-colors duration-300" 
              aria-label="LinkedIn"
            >
              <i className="fab fa-linkedin-in"></i>
            </a>
          </div>
        </div>
      </div>
      
      <div className={cn(
        "container mx-auto px-4 flex flex-wrap items-center justify-between transition-all",
        isScrolled ? "py-2" : "py-4 bg-white shadow-elegant rounded-b-lg"
      )}>
        <div className="flex items-center">
          <Link href="/" className="flex items-center">
            <img 
              src="/images/logos/sw.jpg" 
              alt="SW Accounting Logo" 
              className="h-10 md:h-12 w-auto" 
            />
          </Link>
        </div>
        
        {/* Mobile Menu Button */}
        <button 
          className="lg:hidden block text-[#1a365d] focus:outline-none"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`fas ${isMenuOpen ? 'fa-times' : 'fa-bars'} text-xl`}></i>
        </button>
        
        {/* Navigation Menu */}
        <nav className={cn(
          "w-full lg:flex lg:w-auto lg:items-center transition-all duration-300 mt-4 lg:mt-0",
          isMenuOpen ? "block" : "hidden"
        )}>
          <ul className="flex flex-col lg:flex-row space-y-3 lg:space-y-0 lg:space-x-6">
            <li className="relative group">
              <Link 
                href="/" 
                className={cn(
                  "text-[#1a365d] hover:text-[#7d9b76] font-medium py-2 px-1 inline-block transition-colors duration-300",
                  location === "/" && "text-[#7d9b76] font-semibold"
                )}
                onClick={() => handleLinkClick()}
              >
                Home
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#7d9b76] transition-all duration-300 group-hover:w-full"></span>
              </Link>
            </li>
            <li className="relative group">
              <Link 
                href="/about" 
                className={cn(
                  "text-[#1a365d] hover:text-[#7d9b76] font-medium py-2 px-1 inline-block transition-colors duration-300",
                  location === "/about" && "text-[#7d9b76] font-semibold"
                )}
                onClick={() => handleLinkClick()}
              >
                About
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#7d9b76] transition-all duration-300 group-hover:w-full"></span>
              </Link>
            </li>
            <li className="relative group">
              <Link 
                href="/services" 
                className={cn(
                  "text-[#1a365d] hover:text-[#7d9b76] font-medium py-2 px-1 inline-block transition-colors duration-300",
                  location === "/services" && "text-[#7d9b76] font-semibold"
                )}
                onClick={() => handleLinkClick()}
              >
                Services
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#7d9b76] transition-all duration-300 group-hover:w-full"></span>
              </Link>
            </li>
            <li className="relative group">
              <Link 
                href="/testimonials" 
                className={cn(
                  "text-[#1a365d] hover:text-[#7d9b76] font-medium py-2 px-1 inline-block transition-colors duration-300",
                  location === "/testimonials" && "text-[#7d9b76] font-semibold"
                )}
                onClick={() => handleLinkClick()}
              >
                Testimonials
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#7d9b76] transition-all duration-300 group-hover:w-full"></span>
              </Link>
            </li>
            <li className="relative group">
              <Link 
                href="/blog" 
                className={cn(
                  "text-[#1a365d] hover:text-[#7d9b76] font-medium py-2 px-1 inline-block transition-colors duration-300",
                  location === "/blog" && "text-[#7d9b76] font-semibold"
                )}
                onClick={() => handleLinkClick()}
              >
                Blog
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#7d9b76] transition-all duration-300 group-hover:w-full"></span>
              </Link>
            </li>
            <li className="relative group">
              <Link 
                href="/contact" 
                className={cn(
                  "text-[#1a365d] hover:text-[#7d9b76] font-medium py-2 px-1 inline-block transition-colors duration-300",
                  location === "/contact" && "text-[#7d9b76] font-semibold"
                )}
                onClick={() => handleLinkClick()}
              >
                Contact
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#7d9b76] transition-all duration-300 group-hover:w-full"></span>
              </Link>
            </li>
            <li className="ml-0 lg:ml-2">
              <Link 
                href="/booking" 
                className="bg-[#7d9b76] text-white px-5 py-2 rounded-md hover:bg-[#1a365d] transition-all duration-300 font-medium shadow-elegant inline-flex items-center"
                onClick={() => handleLinkClick()}
              >
                <i className="fas fa-calendar-alt mr-2"></i>
                Book a Meeting
              </Link>
            </li>
            <li>
              {user ? (
                <div className="flex items-center space-x-2">
                  <Link 
                    href={user.role === 'admin' ? '/admin' : '/client-portal'} 
                    className="bg-[#1a365d] text-white px-5 py-2 rounded-md hover:bg-opacity-90 transition-all duration-300 font-medium flex items-center"
                    onClick={() => handleLinkClick()}
                  >
                    <UserCircle className="mr-1 w-5 h-5" />
                    {user.role === 'admin' ? 'Admin Dashboard' : 'Client Portal'}
                  </Link>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-[#1a365d] hover:text-[#7d9b76] flex items-center"
                    onClick={() => logoutMutation.mutate()}
                    disabled={logoutMutation.isPending}
                  >
                    <LogOut className="w-4 h-4 mr-1" />
                    Logout
                  </Button>
                </div>
              ) : (
                <Link 
                  href="/auth" 
                  className="bg-[#1a365d] text-white px-5 py-2 rounded-md hover:bg-opacity-90 transition-all duration-300 font-medium"
                  onClick={() => handleLinkClick()}
                >
                  Client Portal
                </Link>
              )}
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
}
